import { BodyBox, useConfirmationDialog } from '@lbcamden/lib-react';
import { formatDMY, formatTimeString } from '@lbcamden/shared-utils';
import { FunctionComponent } from 'react';
import { useNavigate, useParams } from 'react-router';

import { visitOutcomeDisplayValues } from '~/client/config/display-values/visits';
import { useProperty, useUserStore } from '~/client/hooks/storeHooks';
import { getPropertyBreadcrumbs, toPropertyPage, toVisitEditPage } from '~/client/utils/navigation';

import { itemActionLabel, ItemDetails } from '../../common/ItemDetails';
import { PageTitle } from '../../common/PageTitle';
import { DirectoryUserInfo } from '../../shared/DirectoryUserInfo';
import { PageBreadcrumbs } from '../../shared/PageBreadcrumbs';
import { LoadInNotFoundPage } from '../LoadInNotFoundPage';

export const PropertyVisitPage: FunctionComponent = () => {
  const { propertyId: propertyIdParam, visitId: visitIdParam } = useParams();
  const propertyId = parseInt(propertyIdParam!);

  const navigate = useNavigate();
  const visit = useUserStore()?.get('visits', visitIdParam!);
  const property = useProperty(propertyId);
  const client = useUserStore();

  const deleteVisit = () => {
    client?.handleAndDispatchMessage('deleteVisitLog', visitIdParam!, { visitId: visitIdParam! });
    void navigate(toPropertyPage(propertyIdParam!));
  };

  const { show: showDeleteDialog, confirmationDialog: deleteDialog } = useConfirmationDialog({
    title: 'Delete visit',
    message: 'Are you sure you want to remove this logged visit?',
    onConfirm: deleteVisit,
  });

  if (!property || !visit) {
    return <LoadInNotFoundPage />;
  }

  return (
    <BodyBox>
      <PageTitle title="Visit" />
      <PageBreadcrumbs links={[...getPropertyBreadcrumbs(property), { name: 'Property visit' }]} />
      <ItemDetails
        titleLabel="Property visit"
        title={property.name.trim() || property.address.split(',')[0]}
        actions={[
          {
            key: 'edit',
            children: itemActionLabel('ui/edit-2', 'Edit visit'),
            onClick: () => navigate(toVisitEditPage(propertyIdParam!, visitIdParam!)),
          },
          {
            key: 'delete',
            children: itemActionLabel('ui/cross-circle', 'Delete visit'),
            onClick: showDeleteDialog,
          },
        ]}
        information={[
          ['Date of attempt', formatDMY(visit.timestamp)],
          ['Time of attempt', formatTimeString(visit.timestamp, true)],
          [
            'Outcome of attempt',
            visitOutcomeDisplayValues[visit.outcome as keyof typeof visitOutcomeDisplayValues],
          ],
          // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
          ['Officer', <DirectoryUserInfo camdenAccountName={visit.officerCamdenAccountName} />],
          ['Notes', visit.notes],
        ]}
      />

      {deleteDialog}
    </BodyBox>
  );
};
